/**
 * Lifecycle Dashboard Component
 * Document lifecycle predictions and expiry alerts
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Calendar, AlertTriangle, Clock, FileText, 
  RefreshCw, ArrowRight, CalendarClock, Shield
} from 'lucide-react';
import agenticApi from '../../api/agenticApi';

const LifecycleDashboard = ({ headers, daysAhead = 90 }) => {
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await agenticApi.getExpiringDocuments(daysAhead, headers);
      setExpiringDocs(response.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch lifecycle data:', err);
      setError(err.response?.data?.detail || 'Failed to load lifecycle data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [daysAhead]);

  const getUrgencyColor = (urgency) => {
    const colors = {
      expired: 'bg-red-100 text-red-800 border-red-200',
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[urgency] || colors.low;
  };

  const getUrgencyIcon = (urgency) => {
    if (urgency === 'expired' || urgency === 'critical') {
      return <AlertTriangle className="w-4 h-4 text-red-600" />;
    }
    if (urgency === 'high') {
      return <Clock className="w-4 h-4 text-orange-600" />;
    }
    return <Calendar className="w-4 h-4 text-slate-500" />;
  };

  if (loading && !expiringDocs.length) {
    return (
      <Card className="border-blue-200">
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-3" />
          <p className="text-slate-600">Loading lifecycle predictions...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6 text-center">
          <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <p className="text-red-700">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={fetchData}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Group by urgency
  const grouped = {
    critical: expiringDocs.filter(d => d.urgency === 'critical' || d.urgency === 'expired'),
    high: expiringDocs.filter(d => d.urgency === 'high'),
    medium: expiringDocs.filter(d => d.urgency === 'medium'),
    low: expiringDocs.filter(d => d.urgency === 'low')
  };

  return (
    <div className="space-y-4" data-testid="lifecycle-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-slate-800">Document Lifecycle</h2>
          <Badge variant="outline" className="text-xs">
            Next {daysAhead} days
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={grouped.critical.length > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <AlertTriangle className="w-4 h-4" />
              Critical
            </div>
            <div className={`text-2xl font-bold ${grouped.critical.length > 0 ? 'text-red-600' : 'text-slate-800'}`}>
              {grouped.critical.length}
            </div>
          </CardContent>
        </Card>
        
        <Card className={grouped.high.length > 0 ? 'border-orange-300 bg-orange-50' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Clock className="w-4 h-4" />
              High Priority
            </div>
            <div className={`text-2xl font-bold ${grouped.high.length > 0 ? 'text-orange-600' : 'text-slate-800'}`}>
              {grouped.high.length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Calendar className="w-4 h-4" />
              Medium
            </div>
            <div className="text-2xl font-bold text-slate-800">
              {grouped.medium.length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Shield className="w-4 h-4" />
              Low Priority
            </div>
            <div className="text-2xl font-bold text-slate-800">
              {grouped.low.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Documents List */}
      {expiringDocs.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Documents Requiring Attention
              <Badge className="bg-blue-100 text-blue-800 ml-auto">
                {expiringDocs.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {expiringDocs.map((doc, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${getUrgencyColor(doc.urgency)} flex items-center gap-3`}
                >
                  {getUrgencyIcon(doc.urgency)}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{doc.title}</div>
                    <div className="text-xs text-slate-500">{doc.doc_type}</div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-xs">
                      {doc.days_until_expiry > 0 
                        ? `${doc.days_until_expiry} days`
                        : 'Expired'
                      }
                    </Badge>
                    <div className="text-xs text-slate-500 mt-1">
                      {new Date(doc.expiry_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-6 text-center">
            <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="font-medium text-green-800">All Clear</h3>
            <p className="text-sm text-green-700 mt-1">
              No documents expiring in the next {daysAhead} days.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LifecycleDashboard;
